import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useRealtime, type ConnectionState, type RealtimeFrame } from './realtime';

interface RealtimeApi {
  state: ConnectionState;
  send: (frame: unknown) => void;
  subscribe: (fn: (f: RealtimeFrame) => void) => () => void;
}
const RealtimeContext = createContext<RealtimeApi | null>(null);

/**
 * One realtime connection for the whole signed-in app. Being connected is what makes a person "online" for
 * routing, so it must not depend on which page is open.
 */
export function RealtimeProvider({ token, children }: { token: string | undefined; children: ReactNode }) {
  const listeners = useRef(new Set<(f: RealtimeFrame) => void>());
  const rt = useRealtime(token, (f) => {
    for (const l of listeners.current) l(f);
  });
  const subscribe = useCallback((fn: (f: RealtimeFrame) => void) => {
    listeners.current.add(fn);
    return () => void listeners.current.delete(fn);
  }, []);
  const value = useMemo(
    () => ({ state: rt.state, send: rt.send, subscribe }),
    [rt.state, rt.send, subscribe],
  );
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/** Receives frames from the shared connection while the calling component is mounted. */
export function useRealtimeChannel(onFrame: (f: RealtimeFrame) => void) {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtimeChannel needs a RealtimeProvider');
  const handler = useRef(onFrame);
  handler.current = onFrame;
  const { subscribe } = ctx;
  useEffect(() => subscribe((f) => handler.current(f)), [subscribe]);
  return { state: ctx.state, send: ctx.send };
}
